import { useState, useEffect, useContext } from 'react';
import { useAlert } from '../functions/useAlert';
import { DataContext } from '../contexts/DataProvider';
import {
  fetchUserConnectors, toggleConnectorStatus,
  saveFirefliesCredentials, saveAdviserLogicCredentials,
  disconnectZoom, disconnectCalendly, deleteConnectorById, updateConnector, createConnector,
} from '../services/connectors.service';
import firefliesIcon from '../assets/images/fireflies-transparent-bg.png';
import zoomicon from '../assets/images/zoom.svg';
import calendlyicon from '../assets/images/calendly.svg';

export const INDIVIDUAL_CONNECTORS = [
  {
    name: 'Zoom', id: 'zoom', displayCategory: 'Meeting Tools',
    capabilities: ['MEETING'], description: 'Host meetings under your personal Zoom account.',
    icon: zoomicon, enabled: true, bgColor: 'linear-gradient(135deg, #2D8CFF, #1E6FFF)',
    authType: 'oauth',
    fields: [],
  },
  {
    name: 'Calendly', id: 'calendly', displayCategory: 'Meeting Tools',
    capabilities: ['MEETING', 'CALENDAR'], description: 'Sync availability and create meetings from bookings.',
    icon: calendlyicon, enabled: true, bgColor: 'linear-gradient(135deg, #a9c7f1ff, #a9c7f1ff)',
    authType: 'oauth', fields: [],
  },
  {
    name: 'Fireflies', id: 'fireflies', displayCategory: 'Meeting Tools',
    capabilities: ['MEETING'], description: 'Automatic meeting transcription via Fireflies.ai.',
    icon: firefliesIcon, enabled: true, bgColor: 'linear-gradient(135deg, #201a6b, #3a3476)',
    authType: 'manual', fields: [{ name: 'FIREFLIES_API_KEY', label: 'API Key', type: 'password' }],
  },
  {
    name: 'AdviserLogic', id: 'adviserlogic', displayCategory: 'CRM',
    capabilities: ['CRM'], description: 'Import and sync clients from your AdviserLogic account.',
    icon: 'A', enabled: true, bgColor: 'linear-gradient(135deg, #009688, #4DB6AC)',
    authType: 'manual',
    fields: [
      { name: 'keyUserId', label: 'User Key (KeyUserId)', type: 'password' },
      { name: 'keyPwdSecret', label: 'Password Secret (KeyPwd)', type: 'password' },
      { name: 'paramUID', label: 'Subscriber ID (paramUID)', type: 'text' },
    ],
  },
];

const oauthRedirect = (connectorId: string) => {
  const token = localStorage.getItem('token');
  if (!token) return;
  const returnTo = (window.location.pathname.includes('/organisations') || window.location.pathname.includes('/admin'))
    ? 'organisations' : 'profile';
  window.location.href = `/api/connector/auth/${connectorId}?token=${token}&returnTo=${returnTo}`;
};

export const useConnectorList = () => {
  const { login }: any = useContext(DataContext);
  const [setAlert] = useAlert();
  const userId = login?.data?.id;
  const orgId = login?.data?.orgId;

  const [userConnectorAuth, setUserConnectorAuth] = useState<Record<string, any>>({});
  const [currentConnector, setCurrentConnector] = useState<any>(null);
  const [connectorInputs, setConnectorInputs] = useState<Record<string, any>>({});
  const [isConnectorModalOpen, setIsConnectorModalOpen] = useState(false);
  const [isConnectorLoading, setIsConnectorLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [confirmationAction, setConfirmationAction] = useState({ open: false, type: '', connector: null as any });

  const refetchConnectors = async () => {
    if (!userId) return;
    try {
      const res: any = await fetchUserConnectors(userId);
      setUserConnectorAuth(res);
    } catch {
      // silently ignore — list stays empty
    }
  };

  useEffect(() => { refetchConnectors(); }, [userId]);

  const displayConnectors = INDIVIDUAL_CONNECTORS.map((conn) => {
    const b = userConnectorAuth[conn.id];
    return { ...conn, status: b?.status || false, isConfigured: !!b?._id };
  });

  const groupedConnectors = displayConnectors.reduce((groups, conn) => {
    if (!groups[conn.displayCategory]) groups[conn.displayCategory] = [];
    groups[conn.displayCategory].push(conn);
    return groups;
  }, {} as Record<string, typeof displayConnectors>);

  const openSetupModal = (connector: any) => {
    if (connector.authType === 'oauth') return;
    const existing = userConnectorAuth[connector.id] || {};
    const inputs: Record<string, any> = {};
    connector.fields?.forEach((f: any) => {
      inputs[f.name] = (f.type === 'password' && existing[f.name] === '***CONFIGURED***') ? '••••••••••••' : (existing[f.name] || '');
    });
    setCurrentConnector(connector);
    setConnectorInputs(inputs);
    setIsConnectorModalOpen(true);
    setModalError(null);
  };

  const handleToggle = (e: any, connector: any) => {
    e.stopPropagation();
    const isConfigured = !!userConnectorAuth[connector.id]?._id;
    const currentStatus = userConnectorAuth[connector.id]?.status;
    if (isConfigured) {
      const type = connector.id === 'calendly' && !currentStatus ? 'reconfigure' : (currentStatus ? 'disable' : 'enable');
      setConfirmationAction({ open: true, type, connector });
    } else if (connector.authType === 'oauth') {
      oauthRedirect(connector.id);
    } else {
      openSetupModal(connector);
    }
  };

  const handleOAuthDisconnect = async (connector: any) => {
    try {
      // Dispatch to the correct connector — never assume Zoom. Zoom and Calendly
      // have provider-specific disconnect endpoints (token/webhook cleanup); any
      // other OAuth connector falls back to a generic delete by its stored _id.
      if (connector.id === 'zoom') {
        await disconnectZoom(userId);
      } else if (connector.id === 'calendly') {
        await disconnectCalendly(userId);
      } else {
        const connectorDbId = userConnectorAuth[connector.id]?._id;
        if (!connectorDbId) throw new Error(`${connector.name} is not connected.`);
        await deleteConnectorById(connectorDbId);
      }
      setAlert(`${connector.name} disconnected successfully!`, 'success');
      refetchConnectors();
    } catch (err: any) {
      setAlert(err?.message || `Failed to disconnect ${connector.name}.`, 'error');
    }
  };

  const handleConfirmToggle = async () => {
    const { type, connector } = confirmationAction;
    if (!type || !connector) return;
    if (type === 'reconfigure') {
      setConfirmationAction((prev) => ({ ...prev, open: false }));
      oauthRedirect(connector.id);
      return;
    }
    try {
      await toggleConnectorStatus({ connectorName: connector.name, userId, scope: 'INDIVIDUAL' });
      setAlert(`Connector ${type}d successfully!`, 'success');
      refetchConnectors();
    } catch (err: any) {
      setAlert(err?.message || `Failed to ${type} connector.`, 'error');
    } finally {
      setConfirmationAction((prev) => ({ ...prev, open: false }));
    }
  };

  const handleAuthorizeConnector = async () => {
    if (!currentConnector) return;
    setIsConnectorLoading(true);
    try {
      if (currentConnector.id === 'fireflies') {
        const apiKey = connectorInputs['FIREFLIES_API_KEY'];
        if (apiKey === '••••••••••••') { setIsConnectorModalOpen(false); return; }
        await saveFirefliesCredentials(apiKey);
        setAlert('Fireflies connected successfully!', 'success');
      } else if (currentConnector.id === 'adviserlogic') {
        const { keyUserId, paramUID, keyPwdSecret } = connectorInputs;
        if (keyPwdSecret === '••••••••••••' || keyUserId === '••••••••••••') {
          setAlert('Please re-enter your User Key and Password Secret to verify changes.', 'warning');
          return;
        }
        await saveAdviserLogicCredentials({ keyUserId, paramUID, keyPwdSecret });
        setAlert('AdviserLogic connected successfully!', 'success');
      } else {
        const cleanConfig: Record<string, any> = {};
        currentConnector.fields.forEach((f: any) => {
          const v = connectorInputs[f.name];
          if (v !== undefined && v !== '' && v !== '••••••••••••') cleanConfig[f.name] = v;
        });
        const payload = { connectorName: currentConnector.name, orgId, userId, scope: 'INDIVIDUAL', config: cleanConfig, displayCategory: currentConnector.displayCategory };
        const existingId = userConnectorAuth[currentConnector.id]?._id;
        if (existingId) {
          await updateConnector(existingId, payload);
          setAlert(`${currentConnector.name} updated successfully!`, 'success');
        } else {
          await createConnector(payload);
          setAlert(`${currentConnector.name} connected successfully!`, 'success');
        }
      }
      setIsConnectorModalOpen(false);
      refetchConnectors();
    } catch (err: any) {
      setModalError(err?.data?.message || err?.data?.error || err?.message || `Failed to save ${currentConnector.name} connector`);
    } finally {
      setIsConnectorLoading(false);
    }
  };

  return {
    userConnectorAuth,
    currentConnector, connectorInputs, setConnectorInputs,
    isConnectorModalOpen, setIsConnectorModalOpen,
    isConnectorLoading, modalError, setModalError,
    confirmationAction, setConfirmationAction,
    groupedConnectors,
    openSetupModal, handleToggle, handleConfirmToggle, handleAuthorizeConnector, handleOAuthDisconnect,
  };
};
